import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { rqClient } from "@/shared/api/instance";
import { getNotificationRoute } from "@/shared/model/notification-routes";
import { showPenalty } from "@/shared/model/penalty-notification";
import { websocketClient, NotificationWSMessage } from "@/shared/websocket";

export function useNotificationsSubscription() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = websocketClient.subscribeToNotifications(
      (notification: NotificationWSMessage) => {
        console.log("[WS] Notification:", notification);

        // Penalty notifications use a dialog instead of toast
        if (notification.type === "penalty_issued") {
          const data = notification.data as
            | {
                current_penalty?: number;
                penalties_limit?: number;
                penalties_until_ban?: number;
                auto_bans_count?: number;
                total_penalties?: number;
              }
            | undefined;

          const current = data?.current_penalty ?? 1;
          const limit = data?.penalties_limit ?? 4;
          const untilBan = data?.penalties_until_ban ?? limit - current;
          const autoBansCount = data?.auto_bans_count ?? 0;
          const totalPenalties = data?.total_penalties ?? 1;

          showPenalty({
            reason: notification.message ?? "",
            current,
            limit,
            untilBan,
            autoBansCount,
            totalPenalties,
          });

          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/notifications", {}),
          );
          queryClient.invalidateQueries(
            rqClient.queryOptions(
              "get",
              "/api/v1/notifications/unread_count",
              {},
            ),
          );
          return;
        }

        // Build description (truncate to 150 chars)
        let description = notification.message || "";
        if (description.length > 150) {
          description = description.slice(0, 150) + "...";
        }

        // Get route
        const link = getNotificationRoute(
          notification.notifiable_type,
          notification.notifiable_id,
        );

        // Show toast
        toast.info(notification.title, {
          description,
          action: link
            ? {
                label: (
                  <ExternalLink className="size-3.5 text-muted-foreground" />
                ),
                onClick: () => navigate(link),
              }
            : undefined,
          className: "group",
          actionButtonStyle: {
            backgroundColor: "transparent",
            position: "absolute",
            top: "0.5rem",
            right: "0.5rem",
            padding: "0.25rem",
          },
        });

        // Invalidate queries
        queryClient.invalidateQueries(
          rqClient.queryOptions("get", "/api/v1/notifications", {}),
        );
        queryClient.invalidateQueries(
          rqClient.queryOptions(
            "get",
            "/api/v1/notifications/unread_count",
            {},
          ),
        );

        // Invalidate related queries based on notification type
        if (
          notification.type.includes("purchase") ||
          notification.type.includes("order") ||
          notification.type.includes("reservation") ||
          notification.type === "archive_ready"
        ) {
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/purchases", {}),
          );
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/purchases/stats", {}),
          );
        }

        if (notification.type.includes("sale")) {
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/sales", {}),
          );
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/sales/stats", {}),
          );
        }

        if (
          notification.type.includes("dispute") ||
          notification.type.includes("escrow")
        ) {
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/disputes", {}),
          );
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/disputes/stats", {}),
          );
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/seller/disputes", {}),
          );
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/seller/disputes/stats", {}),
          );
        }

        if (notification.type.includes("review")) {
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/reviews", {}),
          );
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/seller/reviews", {}),
          );
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/seller/reviews/stats", {}),
          );
        }

        if (notification.type.includes("withdrawal")) {
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/withdrawals", {}),
          );
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/withdrawals/stats", {}),
          );
        }

        if (
          notification.type.includes("deposit") ||
          notification.type.includes("transaction")
        ) {
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/stores/transactions", {}),
          );
          queryClient.invalidateQueries(
            rqClient.queryOptions("get", "/api/v1/balance", {}),
          );
        }
      },
    );

    return () => unsubscribe();
  }, [queryClient, navigate]);
}
